import React, { useState, } from 'react';
import api from '../utils/api';
import Alert from '../components/Alert';
import { downloadFile } from '../utils/helpers';
import { 
  Download, 
  FileText, 
  BarChart, 
  FileSpreadsheet, 
  Database, 
  Calendar, 
  Info, 
  Loader2,
  AlertTriangle,
  BarChart3,
  PieChart,
  LineChart,
  Briefcase
} from 'lucide-react';
import './Reports.css';

const Reports = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState({
    dayPDF: false,
    monthPDF: false,
    yearPDF: false,
    daySummary: false,
    monthSummary: false,
    yearSummary: false,
    dayExcel: false,
    monthExcel: false,
    yearExcel: false,
    monthlyReport: false,
    yearlyReport: false,
    backup: false
  });

  // Active Tab State
  const [activeTab, setActiveTab] = useState('daily');

  // Helper to parse blob errors
  const handleDownloadError = async (err, defaultMsg) => {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const data = JSON.parse(text);
        setError(data.message || defaultMsg);
      } catch (e) {
        setError(defaultMsg);
      }
    } else {
      setError(err.response?.data?.message || defaultMsg);
    }
  };

  // Day selection
  const [dayReport, setDayReport] = useState({
    date: new Date().toISOString().split('T')[0]
  });

  // Month selection
  const [monthReport, setMonthReport] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  // Year selection
  const [yearReport, setYearReport] = useState({
    year: new Date().getFullYear()
  });

  // Financial Year selection
  const [yearlyReport, setYearlyReport] = useState({
    financialYear: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`
  });

  // Download Day Invoices (Full PDF)
  const handleDayPDFDownload = async () => {
    try {
      setLoading(prev => ({ ...prev, dayPDF: true }));
      setError('');

      const response = await api.get('/bulk-export/pdf/day', {
        params: { date: dayReport.date },
        responseType: 'blob'
      });

      downloadFile(response.data, `Invoices_${dayReport.date}.pdf`);
      setSuccess('Day invoices PDF downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download day invoices');
    } finally {
      setLoading(prev => ({ ...prev, dayPDF: false }));
    }
  };

  // Download Month Invoices (Full PDF)
  const handleMonthPDFDownload = async () => {
    try {
      setLoading(prev => ({ ...prev, monthPDF: true }));
      setError('');

      const response = await api.get('/bulk-export/pdf/month', {
        params: {
          month: monthReport.month,
          year: monthReport.year
        },
        responseType: 'blob'
      });

      downloadFile(response.data, `Invoices_${monthReport.year}-${String(monthReport.month).padStart(2, '0')}.pdf`);
      setSuccess('Month invoices PDF downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download month invoices');
    } finally {
      setLoading(prev => ({ ...prev, monthPDF: false }));
    }
  };

  // Download Year Invoices (Full PDF)
  const handleYearPDFDownload = async () => {
    try {
      setLoading(prev => ({ ...prev, yearPDF: true }));
      setError('');

      const response = await api.get('/bulk-export/pdf/year', {
        params: { year: yearReport.year },
        responseType: 'blob'
      });

      downloadFile(response.data, `Invoices_${yearReport.year}.pdf`);
      setSuccess('Year invoices PDF downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download year invoices');
    } finally {
      setLoading(prev => ({ ...prev, yearPDF: false }));
    }
  };

  // Download Summary PDF
  const handleSummaryPDF = async (type, params) => {
    try {
      setLoading(prev => ({ ...prev, [`${type}Summary`]: true }));
      setError('');

      const response = await api.get('/bulk-export/pdf/summary', {
        params: { type, ...params },
        responseType: 'blob'
      });

      let filename = 'Summary.pdf';
      if (type === 'day') filename = `Summary_${params.date}.pdf`;
      if (type === 'month') filename = `Summary_${params.year}-${String(params.month).padStart(2, '0')}.pdf`;
      if (type === 'year') filename = `Summary_${params.year}.pdf`;

      downloadFile(response.data, filename);
      setSuccess('Summary PDF downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download summary');
    } finally {
      setLoading(prev => ({ ...prev, [`${type}Summary`]: false }));
    }
  };

  // Download Detailed Excel
  const handleDetailedExcel = async (type, params) => {
    try {
      setLoading(prev => ({ ...prev, [`${type}Excel`]: true }));
      setError('');

      const response = await api.get('/bulk-export/excel/detailed', {
        params: { type, ...params },
        responseType: 'blob'
      });

      let filename = 'Detailed_Invoices.xlsx';
      if (type === 'day') filename = `Detailed_${params.date}.xlsx`;
      if (type === 'month') filename = `Detailed_${params.year}-${String(params.month).padStart(2, '0')}.xlsx`;
      if (type === 'year') filename = `Detailed_${params.year}.xlsx`;

      downloadFile(response.data, filename);
      setSuccess('Detailed Excel downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download Excel');
    } finally {
      setLoading(prev => ({ ...prev, [`${type}Excel`]: false }));
    }
  };

  // Existing functions (keep them)
  const handleMonthlyExport = async () => {
    try {
      setLoading(prev => ({ ...prev, monthlyReport: true }));
      setError('');

      const response = await api.get('/export/monthly', {
        params: {
          month: monthReport.month,
          year: monthReport.year
        },
        responseType: 'blob'
      });

      const monthName = new Date(monthReport.year, monthReport.month - 1).toLocaleString('default', { month: 'long' });
      downloadFile(response.data, `Invoice_Report_${monthName}_${monthReport.year}.xlsx`);
      setSuccess('Monthly report downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download monthly report');
    } finally {
      setLoading(prev => ({ ...prev, monthlyReport: false }));
    }
  };

  const handleYearlyExport = async () => {
    try {
      setLoading(prev => ({ ...prev, yearlyReport: true }));
      setError('');

      const response = await api.get('/export/financial-year', {
        params: {
          financialYear: yearlyReport.financialYear
        },
        responseType: 'blob'
      });

      downloadFile(response.data, `FY_Report_${yearlyReport.financialYear}.xlsx`);
      setSuccess('Financial year report downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download yearly report');
    } finally {
      setLoading(prev => ({ ...prev, yearlyReport: false }));
    }
  };

  const handleFullBackup = async () => {
    try {
      setLoading(prev => ({ ...prev, backup: true }));
      setError('');

      const response = await api.get('/export/backup', {
        responseType: 'blob'
      });

      const timestamp = new Date().toISOString().split('T')[0];
      downloadFile(response.data, `full_backup_${timestamp}.json`);
      setSuccess('Full backup downloaded successfully');
    } catch (err) {
      await handleDownloadError(err, 'Failed to download backup');
    } finally {
      setLoading(prev => ({ ...prev, backup: false }));
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  return (
    <div>
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BarChart3 size={28} /> Reports & Exports
        </h1>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* TABS */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', overflowX: 'auto', paddingBottom: '8px' }}>
        <button 
          onClick={() => setActiveTab('daily')} 
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'daily' ? 'var(--accent)' : 'var(--bg-card)', color: activeTab === 'daily' ? '#fff' : 'var(--text-primary)', fontWeight: '600', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
        >
          Daily Reports
        </button>
        <button 
          onClick={() => setActiveTab('monthly')} 
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'monthly' ? 'var(--accent)' : 'var(--bg-card)', color: activeTab === 'monthly' ? '#fff' : 'var(--text-primary)', fontWeight: '600', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
        >
          Monthly Reports
        </button>
        <button 
          onClick={() => setActiveTab('yearly')} 
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'yearly' ? 'var(--accent)' : 'var(--bg-card)', color: activeTab === 'yearly' ? '#fff' : 'var(--text-primary)', fontWeight: '600', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
        >
          Yearly Reports
        </button>
        <button 
          onClick={() => setActiveTab('financial')} 
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'financial' ? 'var(--accent)' : 'var(--bg-card)', color: activeTab === 'financial' ? '#fff' : 'var(--text-primary)', fontWeight: '600', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
        >
          Financial Year
        </button>
        <button 
          onClick={() => setActiveTab('backup')} 
          style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'backup' ? 'var(--accent)' : 'var(--bg-card)', color: activeTab === 'backup' ? '#fff' : 'var(--text-primary)', fontWeight: '600', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
        >
          Backup & Info
        </button>
      </div>

      {/* DAY REPORTS */}
      {activeTab === 'daily' && (
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={20} /> Daily Reports
          </div>
        </div>
        <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
          Download all invoices for a specific day in various formats
        </p>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Select Date</label>
            <input
              type="date"
              className="form-control"
              value={dayReport.date}
              onChange={(e) => setDayReport({ date: e.target.value })}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
            <button
              className="btn btn-primary"
              onClick={handleDayPDFDownload}
              disabled={loading.dayPDF}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.dayPDF ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {loading.dayPDF ? 'Generating...' : 'Full Invoices PDF'}
              </div>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => handleSummaryPDF('day', { date: dayReport.date })}
              disabled={loading.daySummary}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.daySummary ? <Loader2 size={16} className="animate-spin" /> : <PieChart size={16} />}
                {loading.daySummary ? 'Generating...' : 'Summary PDF'}
              </div>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => handleDetailedExcel('day', { date: dayReport.date })}
              disabled={loading.dayExcel}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.dayExcel ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                {loading.dayExcel ? 'Generating...' : 'Detailed Excel'}
              </div>
            </button>
          </div>
        </div>

        <div style={{ padding: '10px', backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--text-primary)', borderRadius: '5px', marginTop: '10px' }}>
          <strong>Full PDF:</strong> Complete invoices with all details<br />
          <strong>Summary PDF:</strong> Overview with totals and invoice list<br />
          <strong>Detailed Excel:</strong> 3 sheets - Summary, Invoices, Items
        </div>
      </div>

      )}

      {/* MONTH REPORTS */}
      {activeTab === 'monthly' && (
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart size={20} /> Monthly Reports
          </div>
        </div>
        <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
          Download all invoices for a specific month with complete details
        </p>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Select Month</label>
            <select
              className="form-control"
              value={monthReport.month}
              onChange={(e) => setMonthReport({ ...monthReport, month: parseInt(e.target.value) })}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Select Year</label>
            <select
              className="form-control"
              value={monthReport.year}
              onChange={(e) => setMonthReport({ ...monthReport, year: parseInt(e.target.value) })}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={handleMonthPDFDownload}
              disabled={loading.monthPDF}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.monthPDF ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {loading.monthPDF ? 'Generating...' : 'Full Invoices PDF'}
              </div>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => handleSummaryPDF('month', { month: monthReport.month, year: monthReport.year })}
              disabled={loading.monthSummary}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.monthSummary ? <Loader2 size={16} className="animate-spin" /> : <PieChart size={16} />}
                {loading.monthSummary ? 'Generating...' : 'Summary PDF'}
              </div>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => handleDetailedExcel('month', { month: monthReport.month, year: monthReport.year })}
              disabled={loading.monthExcel}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.monthExcel ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                {loading.monthExcel ? 'Generating...' : 'Detailed Excel'}
              </div>
            </button>

            <button
              className="btn btn-primary"
              onClick={handleMonthlyExport}
              disabled={loading.monthlyReport}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.monthlyReport ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {loading.monthlyReport ? 'Generating...' : 'CA Report (Excel)'}
              </div>
            </button>
          </div>
        </div>
      </div>

      )}

      {/* YEAR REPORTS */}
      {activeTab === 'yearly' && (
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LineChart size={20} /> Yearly Reports
          </div>
        </div>
        <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
          Download all invoices for a complete year
        </p>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Select Year</label>
            <select
              className="form-control"
              value={yearReport.year}
              onChange={(e) => setYearReport({ year: parseInt(e.target.value) })}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
            <button
              className="btn btn-primary"
              onClick={handleYearPDFDownload}
              disabled={loading.yearPDF}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.yearPDF ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {loading.yearPDF ? 'Generating...' : 'Full Invoices PDF'}
              </div>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => handleSummaryPDF('year', { year: yearReport.year })}
              disabled={loading.yearSummary}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.yearSummary ? <Loader2 size={16} className="animate-spin" /> : <PieChart size={16} />}
                {loading.yearSummary ? 'Generating...' : 'Summary PDF'}
              </div>
            </button>

            <button
              className="btn btn-primary"
              onClick={() => handleDetailedExcel('year', { year: yearReport.year })}
              disabled={loading.yearExcel}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.yearExcel ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                {loading.yearExcel ? 'Generating...' : 'Detailed Excel'}
              </div>
            </button>
          </div>
        </div>
      </div>

      )}

      {/* FINANCIAL YEAR REPORT */}
      {activeTab === 'financial' && (
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Briefcase size={20} /> Financial Year Report (Excel)
          </div>
        </div>
        <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
          Download complete financial year report with monthly summary
        </p>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Financial Year</label>
            <input
              type="text"
              className="form-control"
              value={yearlyReport.financialYear}
              onChange={(e) => setYearlyReport({ financialYear: e.target.value })}
              placeholder="e.g., 2024-2025"
            />
            <small style={{ color: '#7f8c8d' }}>Format: YYYY-YYYY</small>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={handleYearlyExport}
              disabled={loading.yearlyReport}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {loading.yearlyReport ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {loading.yearlyReport ? 'Generating...' : 'Download Excel'}
              </div>
            </button>
          </div>
        </div>

        <div style={{ padding: '10px', backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--text-primary)', borderRadius: '5px', marginTop: '10px' }}>
          <strong>Includes:</strong> Two sheets - Monthly Summary and Detailed Report
        </div>
      </div>

      )}

      {/* FULL BACKUP */}
      {activeTab === 'backup' && (
      <>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Database size={20} /> Full Data Backup (JSON)
          </div>
        </div>
        <p style={{ color: '#7f8c8d', marginBottom: '15px' }}>
          Download complete database backup for archival purposes
        </p>

        <button
          className="btn btn-primary"
          onClick={handleFullBackup}
          disabled={loading.backup}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {loading.backup ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {loading.backup ? 'Generating...' : 'Download Full Backup'}
          </div>
        </button>

        <div style={{ padding: '10px', backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--text-primary)', borderRadius: '5px', marginTop: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={18} style={{ color: '#fbbf24' }} /> 
          <div><strong>Note:</strong> This includes all invoice data in JSON format. Store securely.</div>
        </div>
      </div>

      {/* INFORMATION */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Info size={20} /> Report Types Explained
          </div>
        </div>
        <div style={{ lineHeight: '1.8' }}>
          <h4 style={{ marginTop: '15px' }}>PDF Reports:</h4>
          <ul>
            <li><strong>Full Invoices PDF:</strong> Complete invoices with all details, one after another - ready to print</li>
            <li><strong>Summary PDF:</strong> Statistical overview with GST breakdown and invoice list</li>
          </ul>

          <h4 style={{ marginTop: '15px' }}>Excel Reports:</h4>
          <ul>
            <li><strong>Detailed Excel:</strong> 3 worksheets - Summary, Invoices with totals, Item-wise breakdown</li>
            <li><strong>CA Report:</strong> Month-wise report formatted for chartered accountants</li>
            <li><strong>FY Report:</strong> Complete financial year with monthly aggregation</li>
          </ul>

          <h4 style={{ marginTop: '15px' }}>Use Cases:</h4>
          <ul>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FileText size={16} /> <strong>Full PDF:</strong> When you need to print/archive physical copies
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <BarChart size={16} /> <strong>Summary PDF:</strong> Quick overview for management review
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FileSpreadsheet size={16} /> <strong>Excel:</strong> Detailed analysis, pivot tables, custom reporting
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Database size={16} /> <strong>JSON Backup:</strong> System migration or disaster recovery
            </li>
          </ul>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Reports;
